import ApiClient from "../client";
import { PaginatedResponse } from "../types";
import {
  ApiCreateQuestPayload,
  ApiQuestBoost,
  ApiQuestBoostPayload,
  ApiQuestBonus,
  ApiQuestBonusPayload,
  ApiQuestCostEstimate,
  ApiQuestCostEstimatePayload,
  ApiQuestDetail,
  ApiQuestEntriesParams,
  ApiQuestEntryLog,
  ApiQuestEntryStaff,
  ApiQuestStats,
  ApiUpdateQuestPayload,
} from "../types/quests";

export const getQuest = (client: ApiClient, questUuid: string) =>
  client.get<ApiQuestDetail>(`/quests/${questUuid}`);

export const createQuest = (client: ApiClient, payload: ApiCreateQuestPayload) =>
  client.post<ApiQuestDetail>("/quests", payload);

export const updateQuest = (
  client: ApiClient,
  questUuid: string,
  payload: ApiUpdateQuestPayload
) => client.patch<ApiQuestDetail>(`/quests/${questUuid}`, payload);

export const deleteQuest = (client: ApiClient, questUuid: string) =>
  client.delete(`/quests/${questUuid}`);

/** A hidden draft copy of the quest, returned in the same shape as `createQuest`. */
export const duplicateQuest = (client: ApiClient, questUuid: string) =>
  client.post<ApiQuestDetail>(`/quests/${questUuid}/duplicate`);

export const estimateQuestCost = (
  client: ApiClient,
  payload: ApiQuestCostEstimatePayload
) => client.post<ApiQuestCostEstimate>("/quests/cost-estimate", payload);

export const getQuestStats = (client: ApiClient, questUuid: string) =>
  client.get<ApiQuestStats>(`/quests/${questUuid}/stats`);

export const createQuestBoost = (
  client: ApiClient,
  questUuid: string,
  payload: ApiQuestBoostPayload
) => client.post<ApiQuestBoost>(`/quests/${questUuid}/boosts`, payload);

export const updateQuestBoost = (
  client: ApiClient,
  questUuid: string,
  boostUuid: string,
  payload: Partial<ApiQuestBoostPayload>
) =>
  client.patch<ApiQuestBoost>(`/quests/${questUuid}/boosts/${boostUuid}`, payload);

export const deleteQuestBoost = (
  client: ApiClient,
  questUuid: string,
  boostUuid: string
) => client.delete(`/quests/${questUuid}/boosts/${boostUuid}`);

export const createQuestBonus = (
  client: ApiClient,
  questUuid: string,
  payload: ApiQuestBonusPayload
) => client.post<ApiQuestBonus>(`/quests/${questUuid}/bonuses`, payload);

export const updateQuestBonus = (
  client: ApiClient,
  questUuid: string,
  bonusUuid: string,
  payload: Partial<ApiQuestBonusPayload>
) =>
  client.patch<ApiQuestBonus>(
    `/quests/${questUuid}/bonuses/${bonusUuid}`,
    payload
  );

export const deleteQuestBonus = (
  client: ApiClient,
  questUuid: string,
  bonusUuid: string
) => client.delete(`/quests/${questUuid}/bonuses/${bonusUuid}`);

/** Every entry on a quest. Venue staff only. */
export const getQuestEntries = (
  client: ApiClient,
  questUuid: string,
  params?: ApiQuestEntriesParams
) => {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  params?.status?.forEach((status) => searchParams.append("status", status));
  if (params?.sort) searchParams.set("sort", params.sort);
  if (params?.page) searchParams.set("page", String(params.page));
  const query = searchParams.toString();
  return client.get<PaginatedResponse<ApiQuestEntryStaff>>(
    `/quests/${questUuid}/entries${query ? `?${query}` : ""}`
  );
};

/** Every entry of a points quest as a CSV file, oldest join first. Venue staff only. */
export const getQuestEntriesCsv = async (
  client: ApiClient,
  questUuid: string
): Promise<Blob> => {
  const response = await client.get(`/quests/${questUuid}/entries.csv`, {
    returnResponse: true,
  });
  return response.blob();
};

/** An entry's credit log. The entry's own user, or staff of the quest's venue. */
export const getQuestEntryLogs = (
  client: ApiClient,
  questUuid: string,
  entryUuid: string,
  searchParams?: URLSearchParams
) => {
  const url =
    `/quests/${questUuid}/entries/${entryUuid}/logs` +
    (searchParams ? `?${searchParams.toString()}` : "");
  return client.get<PaginatedResponse<ApiQuestEntryLog>>(url);
};

export const endpoints = {
  getQuest,
  createQuest,
  updateQuest,
  deleteQuest,
  duplicateQuest,
  estimateQuestCost,
  getQuestStats,
  createQuestBoost,
  updateQuestBoost,
  deleteQuestBoost,
  createQuestBonus,
  updateQuestBonus,
  deleteQuestBonus,
  getQuestEntries,
  getQuestEntriesCsv,
  getQuestEntryLogs,
};
